import type { Plugin } from "chart.js";

export type QuadrantZone = { label: string; fill: string };

/** Options for `quadrantZonesPlugin`, passed as `options.plugins.quadrantZones`. */
export type QuadrantZonesOptions = {
  /** Vertical divider, in x-axis units. */
  x: number;
  /** Horizontal divider, in y-axis units. */
  y: number;
  xLabel: string;
  yLabel: string;
  topLeft: QuadrantZone;
  topRight: QuadrantZone;
  bottomLeft: QuadrantZone;
  bottomRight: QuadrantZone;
};

const INK = "#64748b";

/**
 * Shades the four quadrants around two dashed dividers and labels them, beneath the data.
 * Used by scatter/bubble charts that sort entities into act-on-this quadrants.
 */
export const quadrantZonesPlugin: Plugin = {
  id: "quadrantZones",
  afterDraw(chart, _args, options) {
    const zones = options as QuadrantZonesOptions;
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x || !scales.y || zones.x == null || zones.y == null) return;
    const { left, right, top, bottom } = chartArea;
    const x = Math.min(Math.max(scales.x.getPixelForValue(zones.x), left), right);
    const y = Math.min(Math.max(scales.y.getPixelForValue(zones.y), top), bottom);

    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = zones.topLeft.fill;
    ctx.fillRect(left, top, x - left, y - top);
    ctx.fillStyle = zones.topRight.fill;
    ctx.fillRect(x, top, right - x, y - top);
    ctx.fillStyle = zones.bottomLeft.fill;
    ctx.fillRect(left, y, x - left, bottom - y);
    ctx.fillStyle = zones.bottomRight.fill;
    ctx.fillRect(x, y, right - x, bottom - y);
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.2;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = "700 10px system-ui";
    ctx.fillStyle = INK;
    ctx.fillText(zones.xLabel, x + 5, top + 12);
    ctx.textAlign = "right";
    ctx.fillText(zones.yLabel, right - 7, y - 5);
    const labels: Array<[string, number, number, CanvasTextAlign]> = [
      [zones.topLeft.label, left + 7, top + 16, "left"],
      [zones.topRight.label, right - 7, top + 16, "right"],
      [zones.bottomLeft.label, left + 7, bottom - 8, "left"],
      [zones.bottomRight.label, right - 7, bottom - 8, "right"],
    ];
    labels.forEach(([label, labelX, labelY, align]) => {
      ctx.textAlign = align;
      ctx.fillText(label, labelX, labelY);
    });
    ctx.restore();
  },
};
